import React from "react";
import { useTranslation } from "react-i18next";
import { Sun, Sprout, Bird, Leaf, ShieldAlert, AlertTriangle, Snowflake } from "lucide-react";
import { InfoCard } from "@/components/result/InfoCard";
import { PestIssuesCard } from "@/components/result/PestIssuesCard";
import { AlternativesCard } from "@/components/result/AlternativesCard";
import { CompanionPlantsCard } from "@/components/result/CompanionPlantsCard";
import { UsesCard } from "@/components/result/UsesCard";
import { ExternalLinkConfirm } from "@/components/result/ExternalLinkConfirm";
import { buildPlantLinks } from "@/lib/plantLinks";
import { sunLabel, sunIcon } from "./sunPreference";

/**
 * IdentificationCards — the stacked info-card column below the Result
 * header. Renders in a fixed order: Sun → Dormancy? → Companion →
 * Keystone → Conservation → Pests → Uses → Notes+LearnMore? →
 * Alternatives? → CompanionPlants?.
 *
 * Card visibility rules:
 *  • Dormancy renders only when `id.dormancy.has_dormancy_period` AND
 *    at least one of `months`/`care_tip` is present.
 *  • Notes+LearnMore renders only when there's at least one note OR
 *    one learn-more link.
 *  • Alternatives renders when the plant is non-native OR classified
 *    as a weed (`showAlts` computed by the parent).
 *  • PestIssues, Uses, CompanionPlants each self-hide when empty.
 */
export default function IdentificationCards({
  id,
  scan,
  scanState,
  isEndangered,
  stateNote,
  plantType,
  showAlts,
  alts,
  altsLoading,
  onLoadAlternatives,
  onRegenerateUses,
  regeneratingUses,
}) {
  const { t } = useTranslation();

  const learnMoreLinks = buildPlantLinks({ identification: id, state: scanState });
  const hasNotes = Boolean(id.notes);
  const hasLinks = learnMoreLinks.length > 0;

  return (
    <div className="px-5 mt-6 space-y-4 animate-fade-up">
      <InfoCard title={t("result.sun")} icon={<Sun className="w-4 h-4" strokeWidth={1.5} />} testId="card-sun">
        {id.sun_preference?.length ? (
          <div className="flex flex-wrap gap-2">
            {id.sun_preference.map((s) => (
              <div key={s} className="inline-flex items-center gap-1.5 bg-white border border-[#E8E5DF] rounded-full px-3 py-1.5 text-sm text-leaf-ink">
                {sunIcon(s)} {sunLabel(s, t)}
              </div>
            ))}
          </div>
        ) : (
          <span className="text-leaf-mist">{t("result.sunUnavailable")}</span>
        )}
      </InfoCard>

      {id.dormancy?.has_dormancy_period && (id.dormancy.months || id.dormancy.care_tip) && (
        <InfoCard
          title={t("result.dormancyTitle")}
          icon={<Snowflake className="w-4 h-4" strokeWidth={1.5} />}
          testId="card-dormancy"
        >
          {id.dormancy.months && (
            <p className="text-[13px] uppercase tracking-[0.14em] text-leaf-mist mb-1.5">
              {t("result.dormancyMonthsLabel")} · <span className="normal-case tracking-normal text-leaf-ink">{id.dormancy.months}</span>
            </p>
          )}
          {id.dormancy.care_tip && (
            <p className="text-[14px] leading-relaxed text-leaf-ink">{id.dormancy.care_tip}</p>
          )}
        </InfoCard>
      )}

      <InfoCard title={t("result.companion")} icon={<Sprout className="w-4 h-4" strokeWidth={1.5} />} testId="card-companion">
        {id.companion_plant?.name ? (
          <>
            <p className="font-display text-2xl text-leaf-ink leading-snug">{id.companion_plant.name}</p>
            {id.companion_plant.reason && (
              <p className="mt-2 text-[14px] text-leaf-mist">{id.companion_plant.reason}</p>
            )}
          </>
        ) : (
          <span className="text-leaf-mist">{t("result.companionSolo")}</span>
        )}
      </InfoCard>

      <InfoCard title={t("result.keystoneTitle")} icon={<Bird className="w-4 h-4" strokeWidth={1.5} />} testId="card-keystone">
        {id.is_keystone ? (
          <>
            <p>
              <span className="font-medium text-leaf">{t("result.keystoneIs")}</span>
              {id.keystone_species_support?.length ? t("result.keystoneSupports") : "."}
            </p>
            {id.keystone_species_support?.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2">
                {id.keystone_species_support.map((sp) => (
                  <li key={sp} className="bg-parchment text-leaf-ink rounded-full px-3 py-1 text-sm">{sp}</li>
                ))}
              </ul>
            )}
          </>
        ) : id.is_keystone === false ? (
          <span className="text-leaf-mist">{t("result.keystoneNot")}</span>
        ) : (
          <span className="text-leaf-mist">{t("result.keystoneUnknown")}</span>
        )}
      </InfoCard>

      <InfoCard
        title={t("result.conservationTitle", { state: scanState })}
        icon={isEndangered ? <ShieldAlert className="w-4 h-4" strokeWidth={1.5} /> : <Leaf className="w-4 h-4" strokeWidth={1.5} />}
        testId="card-status"
      >
        {isEndangered ? (
          <p>
            <span className="text-rust font-medium">{t("result.endangeredYes", { state: scanState })}</span>
            {stateNote ? ` ${stateNote}` : ""}
          </p>
        ) : isEndangered === false ? (
          <p className="text-leaf-mist">{t("result.endangeredNo", { state: scanState })}</p>
        ) : (
          <p className="text-leaf-mist">{t("result.statusUnknown")}</p>
        )}
      </InfoCard>

      {/* Common pests + natural remedies. Component returns null when empty. */}
      <PestIssuesCard pests={id.common_pests} />

      {/* Traditional / cultural uses. Component returns null when both
          lists are empty AND there's no regenerate hook. We only wire
          the regenerate hook for saved scans (scan_id present) — on a
          fresh identify with no saved scan there's no doc to update. */}
      <UsesCard
        medicinal={id.medicinal_traditional_uses}
        cultural={id.cultural_uses}
        onRegenerate={scan?.scan_id ? onRegenerateUses : undefined}
        regenerating={regeneratingUses}
      />

      {(hasNotes || hasLinks) && (
        <InfoCard title={t("result.notes")} icon={<AlertTriangle className="w-4 h-4" strokeWidth={1.5} />} testId="card-notes">
          {hasNotes && <p className="text-leaf-mist">{id.notes}</p>}
          {hasLinks && (
            <div className={hasNotes ? "mt-4" : ""}>
              <p className="text-[11px] uppercase tracking-[0.18em] text-leaf font-medium mb-2">
                {t("result.learnMore")}
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="learn-more-links">
                {learnMoreLinks.map((lnk) => (
                  <ExternalLinkConfirm
                    key={lnk.key}
                    href={lnk.url}
                    label={lnk.label}
                    source={lnk.label}
                    testId={`learn-more-${lnk.key}`}
                    scanId={scan?.scan_id}
                    plantName={id.common_name || id.scientific_name}
                  />
                ))}
              </div>
              <p className="mt-2 text-[10px] text-leaf-mist">{t("result.learnMoreHelp")}</p>
            </div>
          )}
        </InfoCard>
      )}

      {showAlts && (
        <AlternativesCard
          alts={alts}
          altsLoading={altsLoading}
          scanState={scanState}
          plantType={plantType}
          onLoad={onLoadAlternatives}
        />
      )}

      {/* Companion-plant suggestions panel — 2-3 species that pair well
          with the identified plant. Card self-hides when the array is
          empty (legacy scans identified before this field existed). */}
      <CompanionPlantsCard
        items={id.companion_plants || []}
        state={scanState}
      />
    </div>
  );
}
